import { Button, Heading, IconButton, InlineLoading, Modal, Section } from '@carbon/react';
import { useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkmark, Close, Printer, QrCode } from '@carbon/icons-react';

import { useGetEntitiesQuery, useRefreshEntityQrMutation } from 'api/entityEndpoints';
import sharedMessages from 'Components/utils/sharedMessages';
import CommonButton from 'Components/Common/CommonButton/CommonButton';
import { useGetUserPermissionForEntityById } from 'hooks/useGetUserPermission';
import styles from './QrCodeModal.module.scss';

type Props = {
    id: string;
}

const QrCodeModal = ({
    id,
}: Props) => {
    const intl = useIntl();
    const { isAdmin, isOwner } = useGetUserPermissionForEntityById(id);
    const { data } = useGetEntitiesQuery();
    const entity = data?.find((e) => e.id === id);
    const printRef = useRef<HTMLDivElement>(null);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [refreshQr, { isLoading }] = useRefreshEntityQrMutation();
    const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);

    const onViewQr = () => {
        setQrCodeModalOpen(true);
    };

    const onModalClose = () => {
        setQrCodeModalOpen(false);
    };

    const onSubmit = () => {
        setQrCodeModalOpen(false);
    };
    
    const handlePrint = () => {
        if (!printRef.current) return;

        const iframe = document.createElement("iframe");
        iframe.style.position = "absolute";
        iframe.style.width = "0px";
        iframe.style.height = "0px";
        iframe.style.border = "0";
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow?.document;
        if (!doc) return;

        doc.open();
        doc.write(`
            <!DOCTYPE html>
            <html lang="pl">
                <head>
                    <meta charset="UTF-8" />
                    <title>Kod QR</title>

                    <style>
                        @page {
                            size: A4;
                            margin: 30mm;
                        }

                        body {
                            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI",
                                        Roboto, Helvetica, Arial, sans-serif;
                            color: #111;
                            margin: 0;
                            padding: 0;
                        }

                        .page {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            min-height: calc(100vh - 60mm);
                            text-align: center;
                        }

                        .header {
                            margin-bottom: 24px;
                        }

                        .header h1 {
                            font-size: 22px;
                            font-weight: 600;
                            margin: 0;
                        }

                        .header p {
                            font-size: 14px;
                            color: #555;
                            margin-top: 6px;
                        }

                        .qr-container {
                            border: 2px solid #000;
                            padding: 24px;
                            border-radius: 8px;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                        }

                        .qr-container img {
                            width: 250px;
                            height: 250px;
                            display: block;
                        }

                        .footer {
                            margin-top: 30px;
                            font-size: 12px;
                            color: #666;
                        }

                        .footer strong {
                            color: #000;
                        }
                    </style>
                </head>

                <body>
                    <div class="page">
                        <div class="header">
                            <h1>Kod QR</h1>
                            <p>Zeskanuj kod, aby uzyskać punkty</p>
                        </div>

                        <div class="qr-container">
                            ${printRef.current.innerHTML}
                        </div>

                        <div class="footer">
                            <p>Wygenerowano: <strong>${new Date().toLocaleDateString()}</strong></p>
                        </div>
                    </div>
                </body>
            </html>
        `);

        doc.close();

        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        document.body.removeChild(iframe);
    };

    const onRefreshQrRequest = () => {
        setConfirmationOpen(true);
    };

    const onRefreshQrCancel = () => {
        setConfirmationOpen(false);
    };

    const onRefreshSubmit = async () => {
        if (!entity) return;

        try {
            setConfirmationOpen(false);
            await refreshQr({ entityId: entity.id });
        } catch {
            // Error handling is managed by RTK Query
        }
    };

    return (
        <>
            <CommonButton
                label={intl.formatMessage(messages.viewQrCode)}
                kind="ghost"
                disabled={!entity}
                icon={<QrCode size={21} />}
                onClick={onViewQr}
            />
            <Modal
                preventCloseOnClickOutside
                open={qrCodeModalOpen}
                onRequestClose={onModalClose}
                onRequestSubmit={onSubmit}
                modalHeading={intl.formatMessage(messages.yourConfirmationQrCode)}
                modalLabel={intl.formatMessage(messages.hereYouCanViewOrGenerate)}
                passiveModal
                closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
            >
                <Section className={styles.modalContent}>
                    <div ref={printRef} className={styles.container}>
                        <Heading className={styles.heading}>{entity?.name}</Heading>
                        <p className={styles.description}>{intl.formatMessage(messages.description)}</p>
                        <img
                            src={`data:image/png;base64,${entity?.qrCodeBase64}`}
                            alt="QR Code"
                            style={{ width: 200, height: 200 }}
                        />
                    </div>
                    <div className={styles.buttons}>
                        <CommonButton
                            label={intl.formatMessage(sharedMessages.print)}
                            kind="ghost"
                            icon={<Printer size={21} />}
                            onClick={handlePrint}
                        />
                        <div className={styles.refreshSection}>
                            <div className={styles.refreshSection}>
                                {confirmationOpen && (
                                    <>
                                        <p className={styles.confirmation}>{intl.formatMessage(messages.areYouSure)}</p>
                                        <div>
                                            <IconButton
                                                kind="secondary"
                                                label={intl.formatMessage(sharedMessages.cancel)}
                                                onClick={onRefreshQrCancel}
                                            >
                                                <Close />
                                            </IconButton>
                                            <IconButton
                                                kind="primary"
                                                disabled={!isAdmin && !isOwner}
                                                align="left"
                                                style={{ backgroundColor: 'red' }}
                                                label={intl.formatMessage(sharedMessages.submit)}
                                                onClick={onRefreshSubmit}
                                            >
                                                <Checkmark />
                                            </IconButton>
                                        </div>
                                    </>
                                )}
                            </div>
                            <Button
                                kind="tertiary"
                                disabled={(!isAdmin && !isOwner) || confirmationOpen || isLoading}
                                renderIcon={isLoading ? InlineLoading : undefined}
                                onClick={onRefreshQrRequest}
                            >
                                {intl.formatMessage(messages.refreshQr)}
                            </Button>
                        </div>
                    </div>
                </Section>
            </Modal>
        </>
    );
};

const messages = defineMessages({
    yourConfirmationQrCode: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.QrCodeModal.QrCodeModal.yourConfirmationQrCode',
        defaultMessage: 'Twój kod QR zatwierdzenia obecności',
    },
    hereYouCanViewOrGenerate: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.QrCodeModal.QrCodeModal.hereYouCanViewOrGenerate',
        defaultMessage: 'Tutaj możesz zobaczyć lub wygenerować nowy kod QR zatwierdzenia obecności.',
    },
    viewQrCode: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.QrCodeModal.QrCodeModal.viewQrCode',
        defaultMessage: 'Zobacz kod QR',
    },
    refreshQr: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.QrCodeModal.QrCodeModal.refreshQr',
        defaultMessage: 'Wygeneruj nowy kod QR',
    },
    description: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.QrCodeModal.QrCodeModal.description',
        defaultMessage: 'Użytkownicy mogą skanować ten kod QR, aby potwierdzić swoją obecność na wydarzeniach organizowanych przez tę grupę.',
    },
    areYouSure: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.QrCodeModal.QrCodeModal.areYouSure',
        defaultMessage: 'Jesteś pewien, że chcesz wygenerować kod QR na nowo? Poprzedni będzie nieaktualny, a ta operacja jest nieodwracalna.',
    },
});

export default QrCodeModal;
